import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Col, DatePicker, Row, Select, Spin } from 'antd';
import { useGetDashboardTargetData } from 'features/dashboard/use-cases/dashboard-target';
import { useGetDashboardCardCommon } from 'features/dashboard/use-cases/get-commonDropDown';
import { FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import graph from 'assets/Dashboard/graph.svg';
const { RangePicker } = DatePicker;

export const RegistrationanalysisstepaView = () => {
  // ✅ Default: today’s date
  const today = dayjs();
  const [dateRange, setDateRange] = useState<[any, any] | null>('' as any);
  const [selectedYear, setSelectedYear] = useState<number>(today.year());
  const [selectedEvent, setSelectedEvent] = useState<any>(undefined);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const chartRef = useRef<HighchartsReact.RefObject>(null);
  const currentYear = today.year();

  // ✅ Prepare payload values
  const payload = {
    Year: selectedYear,
    FromDate: dateRange ? dayjs(dateRange[0]).format('YYYY-MM-DD') : null,
    ToDate: dateRange ? dayjs(dateRange[1]).format('YYYY-MM-DD') : null,
    EventIds: selectedEvent,
    CategoryIds: selectedCategories.length > 0 ? selectedCategories.join(',') : null,
  };

  // 🔹 API Calls
  const { data, isLoading, refetch } = useGetDashboardTargetData(payload);
  const { data: eventsData } = useGetDashboardCardCommon({ Flag: 'events',year:selectedYear });
  const { data: categoriesData } = useGetDashboardCardCommon(
    selectedEvent
      ? { Flag: 'categorylist', MultipleIds: selectedEvent }
      : { enabled: false }
  );

  // 🔹 Year options dynamically (2020 → current year)
  const years = Array.from({ length: 16 }, (_, i) => {
    const year = currentYear - 10 + i;

    return { label: year.toString(), value: year };
  });


  // 🔹 Apply filters
  const handleApplyFilters = () => {
    refetch();
  };

  // 🔹 Reset filters
  const handleReset = () => {
    setSelectedYear(currentYear);
    setSelectedEvent(undefined);
    setSelectedCategories([]);
    setDateRange([today, today]);
    refetch(); // 🔹 call API with reset filters
  };

  // 🔹 Chart config
  const chartOptions: Highcharts.Options = useMemo(() => {
    if (!data?.GraphData) {
      return {
        title: { text: 'Loading...' },
      };
    }

    const categories = data.GraphData.map((item: any) => item.EventName);
    const registered = data.GraphData.map(
      (item: any) => item.TotalRegistration ?? 0
    );
    const target = data.GraphData.map((item: any) => item.Target ?? 0);

    return {
      chart: { type: 'column' },
      title: { text: 'Total Registration per Event' },
      xAxis: { categories, crosshair: true },
      yAxis: {
        min: 0,
        title: { text: "Participants", style: { fontSize: "11px", color: "black" } },
        labels: { style: { fontSize: "11px", color: "black" } },
      },
      tooltip: { shared: true },
      plotOptions: {
        column: { pointPadding: 0.2, borderWidth: 0 },
      },
      credits: { enabled: false },
      colors: ['#008044', '#E55050'],
      series: [
        { name: 'Registered', type: 'column', data: registered },
        { name: 'Target', type: 'column', data: target },
      ],
    };
  }, [data]);

  useEffect(() => {
    const chart = chartRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading("Loading data...");
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  return (
    <Fragment>
      {/* 🔹 Filters Row */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col span={24} style={{ padding: '0' }} >
          <Card className="filtr_card filt_repn">
            {/* Date Range */}
            <RangePicker
              style={{ marginRight: 16 }}
              value={dateRange}
              onChange={(dates: any) => {
                setDateRange(dates);
                setSelectedYear(currentYear);
              }}
            />

            {/* Year Filter */}
            <Select
              options={years}
              value={selectedYear}
              onChange={(value) => {
                setSelectedYear(value);
                setDateRange(null as any);
              }}
              style={{ width: 120, marginRight: 16 }}
              placeholder="Select Year"
            />

            {/* Event Filter */}
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Filter by Event"
              value={selectedEvent}
              onChange={(value) => {
                setSelectedEvent(value);
                setSelectedCategories([]);
              }}
              style={{ width: 150, marginRight: 16 }}
              dropdownRender={(menu) => (
                <div>
                  <div
                    style={{
                      padding: 8,
                      fontWeight: 500,
                      borderBottom: '1px solid #f0f0f0',
                    }}
                  >
                    <FilterOutlined style={{ marginRight: 6 }} />
                    Select Event
                  </div>
                  {menu}
                </div>
              )}
              options={
                eventsData?.dropDownList?.map((e: any) => ({
                  label: e.Name,
                  value: e.Id,
                })) || []
              }
            />
            {/* Category Filter (MULTIPLE) */}
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              placeholder="Filter by Category"
              value={selectedCategories}
              onChange={(values) => setSelectedCategories(values)}
              style={{ width: 220 }}
              dropdownRender={(menu) => (
                <div>
                  <div
                    style={{
                      padding: 8,
                      fontWeight: 500,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    <FilterOutlined style={{ marginRight: 6 }} />
                    Select Category
                  </div>
                  {menu}
                </div>
              )}
              // 👇 This controls how tags are displayed
              maxTagCount={1}
              maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
              options={
                categoriesData?.dropDownList?.map((c: any) => ({
                  label: c.Name,
                  value: c.Id,
                })) || []
              }
              disabled={!selectedEvent}
            />



            {/* Buttons */}
            <Button
              type="primary"
              style={{ marginLeft: "16px", marginRight: "10px" }}
              onClick={handleApplyFilters}
            >
              Filter
            </Button>
            <Button onClick={handleReset}>Cancel</Button>
          </Card>
        </Col>
      </Row>

      {/* 🔹 Summary Cards */}
      <Row gutter={[16, 16]} className='row_gap respn_row'>
        <Col span={6}>
          <div className="sales_regis">
            <Card style={{ backgroundColor: "#7259FE" }}>
              <div>
                <p style={{ fontSize: 14, color: "#fff" }}>
                  Total Participants - Registration
                </p>
              </div>
              <div className="card_img_t">
                <h2 style={{ fontSize: 16, color: "#fff" }}>
                  {data?.TotalParticipants ?? 0}
                </h2>
                <img src={graph} alt='' width={40} height={40} />
              </div>
            </Card>
          </div>
        </Col>
      </Row>

      {/* 🔹 Chart */}
      <Row className='row_gap'>
        <Col span={24}>
          <Spin spinning={isLoading} >
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptions}
              ref={chartRef}
            />
          </Spin>
        </Col>
      </Row>
    </Fragment>
  );
};
